import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { StarMark } from "@/components/Logo";
import { hasPermission, hasAnyPermission, inScope, isSuperuser } from "@/lib/permissions";
import { ForcePasswordChange } from "@/components/ForcePasswordChange";
import { ForceMfaSetup } from "@/components/ForceMfaSetup";
import { Lock } from "lucide-react";

const OwnerRestricted = () => (
  <div className="flex min-h-screen flex-col items-center justify-center bg-ek-canvas px-6 text-center" data-testid="owner-restricted">
    <div className="rounded-full bg-ek-purple/10 p-4">
      <Lock size={28} className="text-ek-purple" />
    </div>
    <h1 className="ek-heading mt-5 text-2xl">Owner access required</h1>
    <p className="mt-2 max-w-md text-sm text-ek-ink/65">
      This area is restricted to the system Owner. Your account has full access to the risk
      platform, but this function is reserved for live-operations administration.
    </p>
    <a href="/dashboard" data-testid="owner-restricted-back" className="ek-btn mt-6 text-sm">
      Back to dashboard
    </a>
  </div>
);

export const ProtectedRoute = ({ children, roles, permission, anyPermission, domain, superuser }) => {
  const { user } = useAuth();

  if (user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ek-canvas">
        <StarMark size={36} className="animate-pulse text-ek-purple" />
      </div>
    );
  }

  if (user === false) {
    return <Navigate to="/login" replace />;
  }

  if (user.must_change_password) {
    return <ForcePasswordChange />;
  }

  if (user.mfa_required && !user.mfa_enabled) {
    return <ForceMfaSetup />;
  }

  if (superuser && !isSuperuser(user)) {
    return <OwnerRestricted />;
  }

  if (roles && !roles.includes(user.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  if (permission && !hasPermission(user, permission)) {
    return <Navigate to="/dashboard" replace />;
  }

  if (anyPermission && !hasAnyPermission(user, anyPermission)) {
    return <Navigate to="/dashboard" replace />;
  }

  if (domain && !inScope(user, domain)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};
